import * as React from "react";
import { inject, observer } from "mobx-react";
import { UitkLink } from "uitk-react-link";
import { AnalyticsStore } from "bernie-core";
import { TopNeighborhoodsProps, DestinationNeighborhoodsViewProps } from "../typings";
import { Neighborhood, TeNeighborhoodFlexModuleResult } from "typings/microserviceModels/te-neighborhood-flex-module";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import {
  NeighborhoodCard,
  NeighborhoodCardFooter,
} from "src/components/flexComponents/DestinationNeighborhoods/components/NeighborhoodCard";
import { TrackedLink } from "src/components/utility/analytics/TrackedLink";
import { IsomorphicCarousel } from "src/components/shared/IsomorphicCarousel/IsomorphicCarousel";
import { useLocalization } from "@shared-ui/localization-context";
import { UitkHeading, UitkText } from "uitk-react-text";
import { HotelSearchLinkBuilder } from "components/utility/HotelSearchLinkBuilder/HotelSearchLinkBuilder";
import { withStores } from "stores";

export const TopNeighborhoods = withStores(
  "thumbor",
  "context",
  "analytics",
  "flexModuleModelStore"
)(
  observer((props: TopNeighborhoodsProps) => {
    const { analytics, context, flexTemplateNode, flexModuleModelStore } = props;

    return (
      <DestinationNeighborhoodsView
        analytics={analytics}
        flexTemplateNode={flexTemplateNode}
        flexModuleModelStore={flexModuleModelStore}
        context={context}
        jumplinkRef={React.createRef()}
      />
    );
  })
);

const DestinationNeighborhoodsView = inject(
  "thumbor",
  "flexModuleModelStore"
)(
  observer((props: DestinationNeighborhoodsViewProps & { analytics?: AnalyticsStore }) => {
    const { flexTemplateNode, flexModuleModelStore } = props;
    const { id, options } = flexTemplateNode;

    const model = flexModuleModelStore.getModel(id) as TeNeighborhoodFlexModuleResult;

    const neighborhoodsKeyHelper: ItemKeyHelper = new ItemKeyHelper("popular-neighborhood");
    const neighborhoods: Neighborhood[] = model?.content?.items;

    const carouselProps = {
      itemsVisible: { lg: 3, md: 2, sm: 1 },
      pageBy: 1,
      peekWidth: 25,
      carouselName: "TopCarousel",
    };

    const keyHelper = new ItemKeyHelper(id);

    const { title, subTitle, learnMore, items } = model.content;
    const { formatText } = useLocalization();

    //When there are less than three neighborhoods the carousel has not to be displayed
    const hasEnoughItems = items.length >= 3;

    if (!hasEnoughItems) {
      return null;
    }

    return (
      <div
        className="DestinationNeighborhoods topNeighborhoods all-y-padding-four"
        id={id}
        data-fm={options.fmId}
        data-fm-title-id={options.fmTitleId}
      >
        <UitkHeading level={2} size={600}>
          {title}
        </UitkHeading>
        <UitkText size={300}>
          {subTitle}{" "}
          {Boolean(learnMore?.url && learnMore?.text) && (
            <UitkLink inline={true} className="preWrap">
              <TrackedLink
                moduleName="TopNeighborhoods"
                href={new HotelSearchLinkBuilder(learnMore.url).build(props.context)}
                linkName="learnMoreAbout"
                rfrr="learnMoreAboutRegion"
              >
                {learnMore.text}
              </TrackedLink>
            </UitkLink>
          )}
        </UitkText>
        <IsomorphicCarousel
          className="all-t-margin-three"
          buttonText={{
            nextButton: formatText("carousel.item.next"),
            prevButton: formatText("carousel.item.prev"),
          }}
          {...carouselProps}
        >
          {neighborhoods?.map((neighborhood: Neighborhood, neighborhoodIndex: number) => {
            const cardProps = {
              className: "uitk-carousel-item fullHeight",
              withBorder: true,
              key: neighborhoodsKeyHelper.next(),
              neighborhood,
              position: neighborhoodIndex + 1,
              context: props.context,
              isDesktop: true,
              isClickeable: false,
            };

            return (
              <NeighborhoodCard {...cardProps} key={keyHelper.next()}>
                <NeighborhoodCardFooter>
                  <UitkLink className="uitk-cell uitk-card-separator-top">
                    <TrackedLink
                      moduleName="TopNeighborhoods"
                      href={new HotelSearchLinkBuilder(neighborhood.callToActionLink).build(props.context)}
                      linkName="findHotelsIn"
                      rfrr={`findHotelsLink.${neighborhoodIndex + 1}`}
                    >
                      {formatText(neighborhood.callToActionLinkLocKey, neighborhood.region.name)}
                    </TrackedLink>
                  </UitkLink>
                </NeighborhoodCardFooter>
              </NeighborhoodCard>
            );
          })}
        </IsomorphicCarousel>
      </div>
    );
  })
);

export default TopNeighborhoods;
